import { MedplumClient } from "@medplum/core";
import { Condition, Procedure } from "@medplum/fhirtypes";

import { EncounterProceduresScope } from "@/encounters/constants/procedures";
import { processWithConcurrencyLimit } from "@/utils/concurrency";
import { collectResourcesFromPaginatedBundle } from "@/utils/pagination";

import { EncounterProceduresVersionChains } from "../types/procedures";

import { getEncounterProceduresAndConditions } from "./procedures";

type FetchEncounterProceduresVersionChainsParams = {
  medplum: MedplumClient;
  encounterId: string;
  clientId: string;
};

/**
 * The procedures tab records the encounter's clinical work, and the biller section records what is
 * being billed on top of it. The tab's amendment diff reads the same side the tab does, so a biller's
 * edit never shows up as a change to the encounter's clinical record.
 */
const PROCEDURES_AMENDMENT_SCOPE = EncounterProceduresScope.Clinical;

/**
 * How many version histories are read at a time.
 *
 * One history read per procedure and per diagnosis is enough to matter on a large encounter, and
 * Medplum prices a history read against the same quota as every other request, so reading them all
 * at once can get the whole diff rate limited. The two resource types are read one group after the
 * other, which keeps the requests in flight at this number rather than twice it.
 */
const VERSION_HISTORY_READ_CONCURRENCY = 5;

/**
 * Reads one procedure's full FHIR version history.
 *
 * A failed page read throws rather than returning the versions read so far: a history missing its
 * older versions reads as a resource that did not exist yet, which the diff would show as a
 * procedure the amendment added.
 *
 * @param medplum - Medplum client.
 * @param id - Procedure id to read the history of.
 * @returns Every stored version, in the order the server returned them.
 */
const readProcedureVersions = async (medplum: MedplumClient, id: string): Promise<Procedure[]> =>
  collectResourcesFromPaginatedBundle<Procedure>(
    medplum,
    await medplum.readHistory("Procedure", id),
    "Procedure",
    { throwOnPageError: true },
  );

/**
 * Reads one diagnosis's full FHIR version history.
 *
 * Throws on a failed page read for the same reason {@link readProcedureVersions} does.
 *
 * @param medplum - Medplum client.
 * @param id - Condition id to read the history of.
 * @returns Every stored version, in the order the server returned them.
 */
const readConditionVersions = async (medplum: MedplumClient, id: string): Promise<Condition[]> =>
  collectResourcesFromPaginatedBundle<Condition>(
    medplum,
    await medplum.readHistory("Condition", id),
    "Condition",
    { throwOnPageError: true },
  );

const toStoredIds = (resources: { id?: string }[]): string[] =>
  resources.map(({ id }) => id).filter((id): id is string => Boolean(id));

/**
 * Reads every version of every procedure and diagnosis the encounter's procedures tab wrote.
 *
 * The tab writes Condition and Procedure resources directly — there is no QuestionnaireResponse
 * behind it — and it edits them with a PUT on the same id, so each resource's own version history is
 * the record of what the amendments did to it.
 *
 * Retired resources are read too. Removing a code retires the resource rather than deleting it, so a
 * removal is only visible to the diff if the read keeps them.
 *
 * @param medplum - Medplum client.
 * @param encounterId - Encounter whose procedures are being diffed.
 * @param clientId - Patient the encounter belongs to.
 * @returns One version chain per resource the encounter holds.
 */
export const fetchEncounterProceduresVersionChains = async ({
  medplum,
  encounterId,
  clientId,
}: FetchEncounterProceduresVersionChainsParams): Promise<EncounterProceduresVersionChains> => {
  const { procedures, conditions } = await getEncounterProceduresAndConditions({
    medplum,
    encounterId,
    clientId,
    scope: PROCEDURES_AMENDMENT_SCOPE,
    includeRetired: true,
  });

  const procedureVersions = await processWithConcurrencyLimit(
    toStoredIds(procedures),
    (id) => readProcedureVersions(medplum, id),
    VERSION_HISTORY_READ_CONCURRENCY,
  );
  const conditionVersions = await processWithConcurrencyLimit(
    toStoredIds(conditions),
    (id) => readConditionVersions(medplum, id),
    VERSION_HISTORY_READ_CONCURRENCY,
  );

  return { procedureVersions, conditionVersions };
};
